import { memo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
// import { AnimatePresence } from 'framer-motion';

import { MODERATOR_PILL_TIMEOUT } from '../../../constants';
import { channel as $channelContent } from '../../../content';
import { CHAT_USER_ROLE } from './useChatConnection/utils';
import { clsm, extractChannelIdfromChannelArn } from '../../../utils';
import { useChat } from '../../../contexts/Chat';
import { useNotif } from '../../../contexts/Notification';
import { useResponsiveDevice } from '../../../contexts/ResponsiveDevice';
import { useUser } from '../../../contexts/User';
// import ChatPopup from './ChatPopup';
import Composer from './Composer';
import ConnectingOverlay from './ConnectingOverlay';
import Messages from './Messages';
import Notification from '../../../components/Notification';
import useResizeObserver from '../../../hooks/useResizeObserver';
import EmojiReactions from '../../../components/EmojiReactions';

const $content = $channelContent.chat;

const Chat = ({ shouldRunCelebration = false }) => {
  const [chatContainerDimensions, setChatContainerDimensions] = useState();
  const emojiReactionsRef = useRef(null);
  const { isSessionValid, userData } = useUser();
  const { notifyError, notifySuccess, notifyInfo, dismissNotif } = useNotif();
  const { isMobileView, isProfileMenuOpen } = useResponsiveDevice();
  const chatSectionRef = useRef();

  const {
    actions,
    chatUserRole,
    hasConnectionError,
    isConnecting,
    sendAttemptError,
    deletedMessageIds,
    deletedMessage,
    setDeletedMessage,
    messages,
    isOnline
  } = useChat();

  // Show offline notification when network goes down
  useEffect(() => {
    if (!isOnline) {
      notifyInfo($content.notifications.info.network_offline, {
        withTimeout: 1000
      });
    } else {
      dismissNotif();
    }
  }, [isOnline, notifyInfo, dismissNotif]);

  const isLoading = isConnecting;
  /**
   * Chat Moderation State and Actions
   */
  const isModerator = chatUserRole === CHAT_USER_ROLE.MODERATOR;
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState({});

  const openChatPopup = useCallback(
    (messageData) => {
      setIsChatPopupOpen(true);
      setSelectedMessage(messageData);
    },
    [setSelectedMessage]
  );

  // Show moderation pill if user role is moderator
  useEffect(() => {
    if (isModerator && !isLoading) {
      setTimeout(
        () =>
          notifyInfo($content.notifications.moderating, {
            timeout: MODERATOR_PILL_TIMEOUT
          }),
        300 // Wait for the loading spinner exit transition to complete
      );
    }
  }, [isModerator, notifyInfo, isLoading]);

  const updateChatContainerDimensions = useCallback((elem) => {
    const { clientWidth, clientHeight } = elem;

    setChatContainerDimensions({ width: clientWidth, height: clientHeight });
  }, []);

  // const handleDeleteMessage = useCallback(() => {
  //   const { id } = selectedMessage;
  //   actions.deleteMessage(id);
  //   deletedMessageIds.current.push(id);

  //   notifySuccess($content.notifications.success.message_removed);
  // }, [selectedMessage, actions, deletedMessageIds, notifySuccess]);

  useResizeObserver(chatSectionRef, (entry) => {
    if (entry) updateChatContainerDimensions(entry.target);
  });

  useEffect(() => {
    if (chatSectionRef.current) {
      updateChatContainerDimensions(chatSectionRef.current);
    }
  }, [chatSectionRef, updateChatContainerDimensions]);

  useEffect(() => {
    if (deletedMessage && !isModerator) {
      const message = messages?.find(({ id }) => id === deletedMessage);
      if (message) {
        const {
          sender: {
            attributes: { channelArn: deletedMessageOwner }
          }
        } = message;
        if (
          extractChannelIdfromChannelArn(deletedMessageOwner.toLowerCase()) ===
          userData?.sub
        )
          notifyError($content.notifications.error.your_message_was_removed);
      }
      setDeletedMessage(undefined);
    }
  }, [
    deletedMessage,
    deletedMessageIds,
    isModerator,
    messages,
    notifyError,
    notifySuccess,
    selectedMessage.id,
    setDeletedMessage,
    userData
  ]);

  return (
    <>
      <div
        ref={chatSectionRef}
        className={clsm([
          (!isProfileMenuOpen || !isChatPopupOpen) && 'relative',
          'flex',
          'flex-1',
          'flex-col',
          'items-center',
          'justify-between',
          'px-0.5'
          // !isOnline && 'opacity-50' // Add opacity when offline
        ])}
        data-testid="chat-component"
      >
        <div className="mt-4 flex w-full justify-start ps-3 z-30">
          <h4 className="font-medium text-lg dark:text-white">
            Live Chat {!isOnline && '(Offline)'}
          </h4>
        </div>
        <div className={clsm(['relative', 'w-full', 'h-auto', 'flex', 'z-50'])}>
          <Notification />
        </div>
        <div className="relative flex-1 w-full">
          <Messages
            isChatPopupOpen={isChatPopupOpen}
            isModerator={isModerator}
            openChatPopup={openChatPopup}
          />
          <div className="absolute inset-0">
            <EmojiReactions
              ref={emojiReactionsRef}
              containerRef={chatSectionRef}
              containerDimensions={chatContainerDimensions}
            />
          </div>
        </div>
        <ConnectingOverlay isLoading={isLoading} />
        {(!isMobileView || isSessionValid) && (
          <div
            className={clsm([
              'flex',
              'flex-row',
              'w-full',
              'pt-5',
              'pb-6',
              'px-[12px]',
              'relative',
              'z-30'
            ])}
          >
            <Composer
              isRequestButtonVisible={false}
              chatUserRole={chatUserRole}
              isDisabled={hasConnectionError}
              isFocusable={!isChatPopupOpen}
              isLoading={isLoading}
              sendAttemptError={sendAttemptError}
              sendMessage={actions.sendMessage}
              onLocalEmojiTriggered={(emoji) => {
                emojiReactionsRef.current?.handleLocalEmoji(emoji);
              }}
            />
          </div>
        )}
      </div>
      {/* <AnimatePresence>
        {isChatPopupOpen && (
          <ChatPopup
            banUser={actions.banUser}
            deleteMessage={handleDeleteMessage}
            isOpen={isChatPopupOpen}
            openChatPopup={openChatPopup}
            parentEl={chatPopupParentEl}
            selectedMessage={selectedMessage}
            setIsChatPopupOpen={setIsChatPopupOpen}
          />
        )}
      </AnimatePresence> */}
    </>
  );
};

Chat.propTypes = {
  shouldRunCelebration: PropTypes.bool
};

export default memo(Chat);
